import { ImgElement, SvgElement } from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import * as lscache from 'lscache';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import arrowIcon from '../../../images/UserMenu.svg';
import { SignInMethod } from '../../../models/signInMethod';
import { authStateStore } from '../../../state-store';
import { clearAllAuthStorage } from '../../../util/helper';
import classes from './Menu.module.scss';

enum AuthActionType {
  ChangePassword = 'Change Password',
  SignOut = 'Sign Out',
  SwitchTenant = 'Switch Tenant',
}

export const Menu: React.FC = () => {
  const tenantAdminName = authStateStore.useState('tenantAdminName');
  const tenantName = authStateStore.useState('tenantName');
  const tenantAdminEmail = authStateStore.useState('tenantAdminEmail');
  const tenantId = authStateStore.useState('tenantId');

  const history = useHistory();
  const [menuVisibility, setMenuVisibility] = useState(false);
  const signInMethod = lscache.get('signInMethod');

  const handleActionClick = (action: string): void => {
    setMenuVisibility(false);

    switch (action) {
      case AuthActionType.ChangePassword:
        history.push('/changePassword');
        break;

      case AuthActionType.SignOut:
        authStateStore.setState({
          tenantAdminName: '',
          tenantAdminEmail: '',
          tenantId: '',
          tenantName: '',
        });
        clearAllAuthStorage();
        history.push('/');
        break;

      case AuthActionType.SwitchTenant:
        authStateStore.setState({
          tenantAdminName: '',
          tenantId: '',
          tenantName: '',
        });

        lscache.set('switchedFromTenantId', lscache.get('currentTenantId'));
        lscache.remove('accessToken');
        lscache.remove('currentTenantId');
        history.push('/');
        break;
    }
  };

  const ssoLogout = async (): Promise<void> => {
    if (config.axinomPortalBaseUrl !== undefined) {
      clearAllAuthStorage();

      // Redirect to the Axinom Portal for sign out.
      window.location.href = new URL(
        '/logout',
        config.axinomPortalBaseUrl,
      ).toString();
    }
  };

  const authActionsList = [];
  if (signInMethod === SignInMethod.DIRECT) {
    authActionsList.push(AuthActionType.ChangePassword);
    authActionsList.push(AuthActionType.SignOut);
  } else if (signInMethod === SignInMethod.SSO) {
    authActionsList.push(AuthActionType.SwitchTenant);
  }

  return (
    <>
      <div className={classes.container}>
        <div className={classes.tenantInfo}>
          <div
            className={classes.tenantMenu}
            onClick={() => {
              setMenuVisibility(!menuVisibility);
            }}
          >
            {/* {tenantName} - {tenantAdminName} */}
            <ImgElement src={arrowIcon} decorative={true} />
          </div>
        </div>
        <div
          className={clsx(
            menuVisibility ? [classes.expanded] : [classes.collapsed],
          )}
        >
          <div className={classes.tenantAdminName}>{tenantAdminName}</div>
          <div className={classes.userField}>
            <b>Email:</b> {tenantAdminEmail}
          </div>
          <div className={classes.userField}>
            <b>Tenant:</b> {tenantName}
          </div>
          <div className={classes.userField}>
            <b>ID:</b> {tenantId}
          </div>

          {authActionsList.map((item) => (
            <span
              className={classes.menuItem}
              key={item}
              onClick={() => handleActionClick(item)}
            >
              {item}
            </span>
          ))}
          {signInMethod === SignInMethod.SSO && (
            <span
              className={classes.menuItemSignOut}
              key="SignOut"
              onClick={ssoLogout}
            >
              Sign Out
            </span>
          )}
        </div>
      </div>
      <div className={classes.logoContainer}>
        <div className={classes.logo}>
          <SvgElement
            viewBox="0 0 145 40"
            title="Axinom Logo"
            aria-label="Axinom Logo"
          >
            <path
              fill="#00467D"
              d="M10.1,22.6c-0.8,2.7-2.2,5.6-4.3,8.7c-0.8,1.2-1.6,1.8-2.3,1.8c-1,0-1.5-0.8-1.7-2.1
c-0.7-5.5,6.4-19.3,9.9-18.5C13.4,12.9,10.6,21.1,10.1,22.6 M3.1,36.4c1.2,0,4-0.7,6.3-6.2c1-1.8,1.3-3,1.7-3.6
c1.2,4.6,3,6.6,5.2,7.1c2.1,0.5,3-0.3,3.8-0.7c2.6-2.1,2.6-2.3-0.6-1.5c-1.8,0.6-3.5-1.2-3.7-1.4c-1.9-2.1-2.4-5-2.4-9.9
c0-0.9,0.1-2.3,0.3-4.2c0.2-1.9,0.3-3.3,0.3-4.2c0-2-0.5-2.9-1.5-2.9c-1.5,0-3.8,2.7-7,8.2c-3.1,5.2-5.1,10.4-5.3,13.7
C0.1,33.3,0.8,36.4,3.1,36.4 M137.4,27.7c2,0.2,3.1-2.1,3.8-6.7c0.9-4.4,1.2-7.1,1.6-8.9c0.7-3.6,1.8-11.2,1.9-12
c0,0-2.9,0.5-2.9,0.6c-0.2,0.8-1.4,6.7-3,12.6c-1.5,5.2-2.3,9.3-2.5,12.4C136.2,26.9,136.6,27.6,137.4,27.7 M55.4,10.8
c0-1.2-0.6-2.1-1.9-2.9c-1.1-0.7-2.2-1-3.3-1c-0.9,0-1.3,0.6-1.3,1.9C48.9,10.1,51,10.8,55.4,10.8 M47.3,35c1.3-0.3,3.2-1.6,3.7-1.9
c1.1-0.8,2.3-1.8,3.3-2.6c0,0,2.8-2.7,3.2-3.2l-0.8-1.7c-1,0.9-3.2,2.7-6.4,4.7c-1,0.6-2.9,2.2-3.5-0.2c-0.4-1.8-0.2-3.6,1.2-7.6
c0.7-2.2,1.5-4.2,1.2-4.4c-0.5-0.4-1.8,0.6-3.9,5.3c-0.8,1.9-2.3,5.2-2.3,7.3C43,35.5,46,35.3,47.3,35 M58.6,36.1
c0.3,0.4,2.3-1.1,3.3-2.8c0.7-1.1,0.6-1.1,0.6-1.1c0.8-1.8,1.3-2.9,1.5-3.3c1.4-2.9,2.5-5,3.2-5.1c0.7-0.1,0.8,0.9,0.9,1.5
c0.1,0.5,0.4,3.6,0.7,4.5c0.8,2.9,1.9,3.5,2.6,4.1c1,0.7,2.3,0.7,3.9,0.6c1-0.1,3-1,3.1-1.4c0-0.1-4.3,0-5.6-2.1
c-1.4-2.2-1.4-9.7-1.6-10.9c-0.2-1.2-0.8-2.5-1.7-2.5c-0.3,0-1.1,0.1-1.7,1.1c-0.8,0.8-1.4,1.2-1.9,1.3c-0.5,0-0.4,0-0.4,0
c-0.1-1.2,0-0.9-0.1-2.1c-0.2-1.5-0.9-2.2-1.8-2.2c-0.9,0-1.2,0-1.5,0c0.3,2.5,0.2,2.5-0.6,8C60.5,30,58.2,35.6,58.6,36.1
 M82.5,32.3c-1.7-0.9-1.5-5.1,1.3-9.7c2-3.2,3.7-3.6,4.2-3.3c0.7,0.5,0.7,2.4-0.3,6C86.7,29,84.2,33.3,82.5,32.3 M84.1,35.1
c1.7,0.1,3.5-2,4.7-5c1.3-3,1.1-10.2,1.1-10.8c0-0.5,0-0.8,0.1-0.9c2.8-0.4,5.1-1.1,6.8-1.9l-0.8-2c0,0-7,1.6-7.8,0.8
c-0.9-0.8-1.7-1.5-2.1-0.8c-0.4,0.7-4.2,5.6-5.1,8.4c-1.6,4.8-1,7.8-0.3,9.5C81.4,34.2,81.9,35,84.1,35.1 M108.5,35.8
c0.4,1.2,0.7,1.6,1.5,1.6c1,0,2.1-1.1,3.1-3.3c4.5-7.5,5-9.6,6.5-9.3c1.2,0.2,1.1,3.5,1.4,6c0.5,4.6,5,5.3,7.2,3.7
c0.9-0.6,1.4-1.7,1.4-1.7s-3,0.5-4.5-0.6c-1.5-1-1.8-2.6-1.9-4.6c-0.1-2.8-0.8-7.1-2.5-7.1c-1.3-0.1-2,0.9-3.2,2.9
c-1.7,3.1-3,5.3-5.8,5c-0.4-2.6-0.2-6.1-0.2-5c-0.1-1.7-0.3-4.3-0.9-5.1c-1.1-1.4-3-1.3-4.7,0.5c-1.3,1-1.7,3.4-3.9,2.8
c0,0,0-2.1,0-2.4c-0.1-1.3-0.4-3.5-1.7-3.5h-1.4c0,1.8,0.2,5.1-0.4,8.9c-0.4,5.2-3.6,11-3,11.3c0.6,0.3,2.5-1.5,3.6-3.1
c1-1.6,2.3-4.1,3.9-6.6c2.4-3.5,3.2-5.3,4.2-5.3c1,0,0.8,1.1,0.8,3.4C107.8,25.3,108.1,34.6,108.5,35.8 M139.6,36.9
c0.1-1.2-0.5-2.2-1.7-3c-1-0.8-2.1-1.2-3.3-1.3c-0.9-0.1-1.4,0.5-1.5,1.8C133.1,35.7,135.2,36.6,139.6,36.9 M21.5,40
c0.6,0,1.5-1,2.7-3c2.2-3.7,3.3-5.5,3.3-5.6c1.4-2,2.6-3.1,3.8-3c1.4,0.1,1.6,1.2,2.6,2.8c1.5,2.2,2.6,4.1,3.4,5.2l1.4-3.3
c-1-1.3-0.8-0.9-2.4-3.1c-1.6-2.2-2.4-3.9-2.4-5.1c0-1,3.1-5.4,5.8-7.9c1.7-1.6,3-3.2,3-3.2L42,13.1c-0.1,0-1,0.7-2.3,1.8
c-3.3,2.6-6.2,6.9-6.7,7.4c-0.7-0.5-1.7-2.1-2.8-4.5c-1-2.4-1.7-3.9-2.6-4.4c-0.8,0-1.1,0.5-1.1,1.4c0,1.1,0.5,2.9,1.6,5.2
c0.4,0.9,2.2,4.2,1.9,5.2c-0.5,1.3-4.9,5.2-5.4,6c-2.6,3.1-3.8,5.5-3.8,7.3C20.7,39.5,20.9,40,21.5,40"
            />
          </SvgElement>
        </div>
      </div>
    </>
  );
};
